import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  newCustomerAtom,
  showValidationErrorsAtom
} from '../atoms/reviewCustomer.atoms'
import { useCallback, useEffect, useMemo } from 'react'
import {
  isValidatingAtom,
  newCustomerValidationAtom
} from '../atoms/reviewCustomer.primitives.atoms'
import {
  CustomerFormFields,
  CustomerFormFieldsData,
  useCustomerFormFields
} from 'Customer/components/CustomerFormFields'
import { HighlightFieldPathsOnHover } from '../../../HighlightFieldPathsOnHover'
import {
  mapAtomToFormData,
  mapFormDataToAtom
} from './newCustomerForm.adapters'

export const NewCustomerForm = () => {
  const [formData, setFormData] = useAtom(newCustomerAtom)
  const setValidationState = useSetAtom(newCustomerValidationAtom)
  const setIsValidating = useSetAtom(isValidatingAtom)
  const showValidationErrors = useAtomValue(showValidationErrorsAtom)

  const initialData = useMemo(
    () => mapAtomToFormData(formData),
    // Only use initial atom value — don't reinitialize on every change
    []
  )

  const handleChange = useCallback(
    (data: CustomerFormFieldsData) => {
      setFormData(mapFormDataToAtom(data))
    },
    [setFormData]
  )

  const handleValidationChange = useCallback(
    (isValid: boolean) => {
      setValidationState({ isValid })
    },
    [setValidationState]
  )

  const { fields, customProperties } = useCustomerFormFields({
    initialData,
    onChange: handleChange,
    onValidationStateChange: handleValidationChange,
    isANewCustomer: true,
    showValidationErrors
  })

  // The step framework gates its "Next" button on `isValidatingAtom`; feed the
  // form's duplicate-name lookup state into it so Next stays disabled while a
  // lookup is in flight.
  const isValidatingName = fields.legalName.isValidating
  useEffect(() => {
    setIsValidating(isValidatingName)
    return () => setIsValidating(false)
  }, [isValidatingName, setIsValidating])

  return (
    <HighlightFieldPathsOnHover
      fieldPaths={[
        'customer.legalName',
        'customer.contacts.*',
        'customer.address.*',
        'customer.taxStatus'
      ]}
    >
      <CustomerFormFields>
        <CustomerFormFields.AccountInformation
          data-testid="customer.new.accountInformation"
          legalNameField={fields.legalName}
          contactsField={fields.contacts}
          autoFocus
        />
        <CustomerFormFields.BillingInformation
          data-testid="customer.new.billingInformation"
          addressFields={fields}
        />
        <CustomerFormFields.Tax
          data-testid="customer.new.tax"
          taxStatusField={fields.taxStatus}
          taxRegistrationsField={fields.taxRegistrations}
        />
        <CustomerFormFields.OtherInformation
          data-testid="customer.new.otherInformation"
          labelField={fields.label}
          parentIdField={fields.parentId}
          aliasesField={fields.aliases}
          customProperties={customProperties}
        />
      </CustomerFormFields>
    </HighlightFieldPathsOnHover>
  )
}
